/**
 * @typedef {object} SiteMapNode
 * @property {string} path
 * @property {string} title
 * @property {string} [icon]
 * @property {string} [helpIconMessage]
 * * @property {boolean} [hasPageTitleOverride]
 * @property {boolean} [excludeFromMenu]
 * @property {boolean} [isConfigPortal]
 * @property {SiteMapNode[]} [children]
 */

import messages from "../helpIconMessages";

/** @type {SiteMapNode[]} */
export default {
  routes: [
    {
      path: "/snapshot",
      title: "Snapshot",
    },
    {
      path: "/reviews/summary",
      title: "Reviews",
      children: [
        {
          path: "/reviews/summary",
          title: "Summary",
          icon: "far fa-chart-line",
        },
        {
          path: "/reviews",
          title: "Reviews",
          icon: "fas fa-comment",
          exact: true,
        },
        {
          path: "/reviews/details",
          title: "Reviews",
          excludeFromMenu: true
        },
        {
          path: "/reviews/storeComparison",
          title: "Store Comparison",
          icon: "far fa-balance-scale",
          hasPageTitleOverride: true
        },
      ],
    },
    {
      path: "/social",
      title: "Social",
      children: [
        {
          path: "/social/summary",
          title: "Summary",
          icon: "far fa-chart-line",
          children: [
            {
              path: "/social/summary/metrics",
              title: "Metrics",
              hasPageTitleOverride: true,
            },
            {
              path: "/social/summary/topPerformingPosts",
              title: "Top Performing Posts",
              hasPageTitleOverride: true,
            },
            {
              path: "/social/summary/providerBreakdown",
              title: "Provider Breakdown",
              hasPageTitleOverride: true,
            },
          ],
        },
        {
          path: "/social/feed",
          title: "Activity",
          icon: "fas fa-comments",
          children: [
            {
              path: "/social/activity",
              title: "Posts And Comments",
              hasPageTitleOverride: true
            },
            {
              path: "/social/hashtagMetrics",
              title: "Hashtag Metrics",
            },
          ],
        },
        {
          path: "/social/approvals",
          title: "Reply Approvals",
          icon: "fas fa-check-circle",
        },
        {
          path: "/social/publishing",
          title: "Publish",
          icon: "fas fa-paper-plane",
        },
        {
          path: "/social/reports",
          title: "Store Comparison",
          icon: "far fa-balance-scale",
          hasPageTitleOverride: true
        },
        {
          path: "/social/settings",
          title: "Settings",
          icon: "fas fa-cog",
        }
      ],
    },
    {
      path: "/pulse",
      title: "Pulse",
      children: [
        {
          path: "/pulse/summary",
          title: "Summary",
          icon: "far fa-chart-line",
          hasPageTitleOverride: true
        },
        {
          path: "/pulse/explorer",
          title: "Sentiment Explorer",
          icon: "fas fa-rocket",
          hasPageTitleOverride: true
        },
        {
          path: "/pulse/storeComparison",
          title: "Store Comparison",
          icon: "far fa-balance-scale",
          hasPageTitleOverride: true
        },
      ],
    },
    {
      path: "/media",
      title: "Media",
      icon: "fas fa-image",
    },
    {
      path: "/listings",
      title: "Listings",
      children: [
        {
          path: "/listings/accuracy",
          title: "Listing Accuracy",
          icon: "far fa-bullseye",
        },
        {
          path: "/listings/summary",
          helpIconMessage: messages.listingSummary,
          title: "Summary",
          icon: "far fa-chart-line",
        },
        {
          path: "/listings/listingOptimization",
          helpIconMessage: messages.listingOptimization,
          title: "Listing Optimization",
          icon: "fas fa-puzzle-piece",
        },
        {
          path: "/listings/missingListings",
          title: "Missing Listings",
          icon: "fas fa-map-marker-alt",
        },
        {
          path: "/listings/duplicateListings",
          helpIconMessage: messages.duplicateListings,
          title: "Duplicate Listings",
          icon: "fas fa-copy",
        },
        {
          path: "/listings/searchAnalyticsReport",
          title: "Search Analytics Report",
          icon: "far fa-search",
        },
        {
          path: "/listings/searchAnalyticsStoreComparison",
          title: "Search Analytics Location Comparison",
          icon: "far fa-trophy",
        },
        {
          path: "/listings/localPagesReport",
          title: "Local Pages",
          icon: "fas fa-address-card"
        }
      ],
    },
    {
      path: "/rankings",
      title: "Rankings",
      children: [
        {
          path: "/rankings/avgRankings",
          helpIconMessages: messages.avgSEORankings,
          title: "Average Rankings",
          icon: "far fa-balance-scale",
        },
        {
          path: "/rankings/page1Rankings",
          title: "Page 1 Rankings",
          helpIconMessages: messages.page1SEORankings,
          icon: "fas fa-trophy",
          hasPageTitleOverride: true
        },
      ],
    },
    {
      path: "/reviewBuilder",
      title: "Review Builder",
      icon: "fas fa-star",
      children: [
        {
          path: "/reviewBuilder",
          hasPageTitleOverride: true,
          exact: true,
          excludeFromMenu: true,
          title: "",
          icon: "fas fa-star",
        }
      ]
    },
    {
      path: "/workflow",
      title: "Workflow",
      children: [
        {
          path: "/workflow2",
          exact: true,
          title: "Tasks",
          icon: "far fa-tasks",
        },
        {
          path: "/workflow2/taskReport",
          title: "Task Report",
          icon: "ss ss-clipboard",
          hasPageTitleOverride: true
        },
        {
          path: "/workflow2/userReport",
          title: "User Report",
          icon: "fas fa-user",
          hasPageTitleOverride: true
        },
        {
          path: "/workflow2/settings",
          title: "Settings",
          icon: "fas fa-cog",
        },
      ],
    },
    {
      path: "/analytics",
      title: "Analytics",
      children: [
        {
          path: "/analytics/reports",
          title: "Reports",
          icon: "fas fa-file",
          children: [
            {
              path: "/analytics/reports/mine",
              title: "My Reports"
            },
            {
              path: "/analytics/reports/shared",
              title: "Shared with Me"
            }
          ]
        },
        {
          path: "/analytics/select-template",
          excludeFromMenu: true,
          title: "Select a Report",
          helpIconMessage: messages.selectReportTemplate
        },
        {
          path: "/analytics/create/:templateId",
          hasPageTitleOverride: true,
          title: "Create Report",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/:reportId/:viewMode(edit|view)",
          hasPageTitleOverride: true,
          excludeFromMenu: true,
        },
        {
          path: "/analytics/create-component",
          title: "Create Component",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/list-data-source",
          itle: "Data Sources",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/create-data-source",
          title: "Create Data Source",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/edit-data-source/:dataSourceId",
          title: "Edit Data Source",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/query-builder",
          title: "Query Builder",
          icon: "far fa-code",
        },
        {
          path: "/analytics/bi-integration",
          hasPageTitleOverride: true,
          title: "BI Integration",
          icon: "fas fa-lightbulb",
        },
      ],
    },
    {
      path: "/admin",
      title: "My Account",
      excludeFromMenu: true,
      children: [
        {
          path: "/admin/v2/user",
          title: "My Profile",
          icon: "fas fa-user",
          hasPageTitleOverride: true
        },
        {
          path: "/admin/v2/users",
          title: "Manage Users",
          icon: "fas fa-users",
          hasPageTitleOverride: true
        },
        {
          path: "/admin/v2/groups",
          title: "Manage Groups",
          icon: "fas fa-map-marker-plus",
          hasPageTitleOverride: true
        },
        {
          path: "/admin/v2/manageScoring",
          title: "Manage Scoring",
          icon: "fas fa-trophy",
        },
        {
          path: "/admin/v2/locations",
          title: "Manage Locations & Competitors",
          hasPageTitleOverride: true,
          icon: "fas fa-map-marker-alt",
        },
        {
          path: "/admin/v2/accounts",
          hasPageTitleOverride: true,
          title: "Manage Accounts",
          icon: "fas fa-globe-americas",
        },
        {
          path: "/admin/v2/brand",
          title: "Brand Settings",
          icon: "fas fa-paint-brush-alt",
        },
        {
          path: "/admin/v2/userRoles",
          title: "User Roles",
          icon: "fas fa-wrench",
          hasPageTitleOverride: true
        },
        {
          path: "/chatmeterAdminRedirect",
          title: "Manage Billing Accounts",
          icon: "far fa-dollar-sign",
        },
        {
          path: "/admin/v2/customProviders",
          title: "Custom Providers",
          icon: "fas fa-th-list",
        },
        {
          path: "/admin/v2/credentials",
          title: "External Logins",
          icon: "fas fa-key",
        },
        {
          path: "/admin/v2/upload",
          title: "Upload Tool",
          icon: "fas fa-cloud-upload",
        },
        {
          path: "/admin/v2/manageTemplates",
          title: "Local Pages Templates",
          icon: "fas fa-cloud-upload"
        },
        {
          path: "/admin/v2/localPagesUploader",
          title: "Upload Local Pages",
          icon: "fas fa-cloud-upload"
        },
        {
          path: "/admin/v2/changeLogs",
          title: "Support Tools",
          icon: "fas fa-search-plus"
        },
        {
          path: "/admin/v2/notificationSettings",
          title: "Notification Settings",
          icon: "fas fa-bell",
        },
        {
          path: "/admin/v2/instantAuditsUsage",
          title: "Instant Audit Usage",
          icon: "far fa-chart-bar",
        },
        {
          path: "/admin/v2/manageLocationsNew",
          excludeFromMenu: true,
        },
      ],
    },
    {
      path: "/chatmeterAdmin",
      title: "Billing Admin",
      excludeFromMenu: true,
      // // healthmeter navigation needs to be refactored out of the nav
      // healthmeter
      // listing health stats // <- kill
      // billing accounts
      // yelp subscriptions
      // LLM recent locations
      // keywords
      // verticals
      // advice local back-sync
      // manage tool
      // listing summary
      // llm publishing
    },
    {
      path: "/scores",
      title: "LBV Scores",
      excludeFromMenu: true,
      children: [
        {
          path: "/scores/summary",
          title: "LBV Score Summary",
          icon: "far fa-chart-bar",
          helpIconMessage: messages.lbvScoreSummary
        },
        {
          path: "/scores/storeComparison/locations",
          title: "Store Comparison",
          icon: "ss ss-buildings",
        },
        {
          path: "/scores/storeComparison/groups",
          title: "Group Comparison",
          icon: "ss ss-warehouse",
        },
        {
          path: "/scores/manageScoringRedirect",
          title: "Scoring Settings",
          icon: "fas fa-cog",
        },
      ],
    },
    {
      path: "/emailAlerts",
      title: "My Alerts",
      excludeFromMenu: true,
      children: [
        {
          path: "/emailAlerts/manage",
          title: "Manage Alerts",
          icon: "fas fa-cog",
        },
        {
          path: "/emailAlerts/manage/:alertId/edit",
          title: "Email Reports",
          excludeFromMenu: true,
          icon: ""
        },
        {
          path: "/emailAlerts/manage/:alertId/copy",
          title: "Email Reports",
          excludeFromMenu: true,
          icon: ""
        },
        {
          path: "/emailAlerts/manage/add",
          title: "Email Reports",
          excludeFromMenu: true,
          icon: ""
        },
      ],
    }
  ]
}
